/*!
 * Copyright © 2025. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { AuthHttpService, AuthService } from '@fleetmatics/ui.authentication';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { isElementNull } from '../../../../utils';
import { ITokenService } from './token.service.interface';

@Injectable({
  providedIn: 'root'
})
export class TokenService implements ITokenService {
  constructor(
    private readonly _authService: AuthService,
    private readonly _authHttpService: AuthHttpService
  ) {}

  public decodeJwt(token: string): JwtPayload {
    return jwtDecode(token);
  }

  public isTokenExpiring(token: string): boolean {
    const decodedToken = this.decodeJwt(token);
    const expiryMs = decodedToken.exp * 1000;
    const nowMs = Date.now();

    // Consider token as expiring if less than 1 minute remaining
    return expiryMs < nowMs + 60_000;
  }

  public async refreshToken(): Promise<string | undefined | null> {
    const authToken = await this._authService.getAuthToken();
    const refreshToken = this._authService.getRefreshToken();
    if (isElementNull(authToken) || isElementNull(refreshToken) || !this.isTokenExpiring(authToken)) {
      // Not necessary or unable to refresh the token (e.g. missing refresh token)
      return authToken;
    }

    const tokens = await this._authHttpService.refreshToken(refreshToken).toPromise();
    this._authService.setAuthToken(tokens.access_token, tokens.expires_in);
    this._authService.setRefreshToken(tokens.refresh_token);

    return tokens.access_token;
  }
}
